import type { SupabaseClient } from '../../lib/supabase';

/**
 * `@` inside a note: reaching for something that already exists in the hub.
 *
 * Three kinds of record, one list. The point is that a note about a job can
 * point AT the job rather than repeating its name and drifting out of date —
 * so a mention carries an id and resolves to a URL, and the label is only
 * what it looked like when you typed it.
 *
 * ── What is searched, and what is not ──────────────────────────────────────
 *
 * Projects, tasks, and CG orders. Every one of those is already behind its own
 * Row Level Security, and this searches them as the signed-in person — so the
 * list can only ever contain records they could have opened anyway. There is
 * no SECURITY DEFINER function here on purpose: a mention picker that could
 * see more than its user is a directory of everything, searchable.
 *
 * CG orders are included but only for CG members, because cg_orders is gated
 * and a non-member's query returns nothing. Asking anyway would be three
 * round trips where two would do.
 */

export type MentionKind = 'project' | 'task' | 'order' | 'deal' | 'lead';

export interface MentionItem {
  id: string;
  kind: MentionKind;
  label: string;
  /** Where the badge links to. */
  href: string;
  /** Shown beside the label so two similarly-named records are separable. */
  hint: string;
}

const KIND_LABELS: Record<MentionKind, string> = {
  project: 'Project',
  task: 'Task',
  order: 'CG Order',
  deal: 'Deal',
  lead: 'Lead',
};

export function kindLabel(kind: MentionKind): string {
  return KIND_LABELS[kind];
}

/**
 * PostgREST's `ilike` wildcard, with the user's own wildcards defanged.
 *
 * A person typing "50%" into the picker means a literal percent. Without
 * escaping it becomes "match anything", and the list fills with records that
 * have nothing to do with what they typed.
 */
export function likeTerm(query: string): string {
  const escaped = query.trim().replace(/[\\%_]/g, (char) => `\\${char}`);
  return `%${escaped}%`;
}

/** How many of each kind. Small on purpose — a picker is not a search page. */
const PER_KIND = 5;

export interface MentionSources {
  /** Whether to ask cg_orders at all. */
  cgMember: boolean;
  /**
   * Whether to ask deals at all — the same gate as the Deals module
   * (tap_may_reach_deals), read once by the caller and passed down rather
   * than queried per keystroke.
   */
  dealAccess?: boolean;
  /** Whether to ask leads at all — the sales team gate (is_sales_member). */
  salesMember?: boolean;
}

export async function searchMentions(
  client: SupabaseClient,
  query: string,
  sources: MentionSources,
): Promise<MentionItem[]> {
  const trimmed = query.trim();
  if (trimmed === '') {
    return [];
  }
  const term = likeTerm(trimmed);

  // Each lookup is wrapped in an async function rather than chained off the
  // builder: PostgREST's builder is PromiseLike, not a Promise, so .then()
  // gives back something Promise.all cannot be typed against.
  const lookups: Promise<MentionItem[]>[] = [
    (async (): Promise<MentionItem[]> => {
      const { data } = await client
        .from('projects')
        .select('id, name')
        .ilike('name', term)
        // A room (Drop 3a) is a project for Discussion's sake only. Without
        // this filter every one of the three seeded rooms would show up as
        // a "project" the moment its name matched, in the @ picker AND the
        // new # picker on the Messages page (plan-drop3-dms-groups.md D7,
        // plan-drop3-extras.md H3).
        .eq('is_room', false)
        .limit(PER_KIND);
      return (data ?? []).map((row) => ({
        id: row.id,
        kind: 'project' as const,
        label: row.name,
        href: `/projects/${row.id}`,
        hint: KIND_LABELS.project,
      }));
    })(),
    (async (): Promise<MentionItem[]> => {
      const { data } = await client
        .from('tasks')
        .select('id, title, project_id')
        .ilike('title', term)
        .limit(PER_KIND);
      return (data ?? []).map((row) => ({
        id: row.id,
        kind: 'task' as const,
        label: row.title,
        // Tasks open inside their project; there is no standalone task URL.
        href: `/projects/${String(row.project_id)}`,
        hint: KIND_LABELS.task,
      }));
    })(),
  ];

  if (sources.cgMember) {
    lookups.push(
      (async (): Promise<MentionItem[]> => {
        const { data } = await client
          .from('cg_orders')
          .select('id, order_no, client')
          .or(`order_no.ilike.${term},client.ilike.${term}`)
          .limit(PER_KIND);
        return (data ?? []).map((row) => ({
          id: row.id,
          kind: 'order' as const,
          label: `${row.order_no} ${row.client}`.trim(),
          href: '/cg-orders',
          hint: KIND_LABELS.order,
        }));
      })(),
    );
  }

  if (sources.dealAccess === true) {
    lookups.push(
      (async (): Promise<MentionItem[]> => {
        // deals is gated by RLS (tap_may_reach_deals): a caller without the
        // module still gets zero rows here even if dealAccess were somehow
        // wrong, so this is belt-and-braces, not the real guard.
        const { data } = await client
          .from('deals')
          .select('id, name')
          .ilike('name', term)
          .limit(PER_KIND);
        return (data ?? []).map((row) => ({
          id: row.id,
          kind: 'deal' as const,
          label: row.name,
          href: `/deals?deal=${row.id}`,
          hint: KIND_LABELS.deal,
        }));
      })(),
    );
  }

  if (sources.salesMember === true) {
    lookups.push(
      (async (): Promise<MentionItem[]> => {
        // leads is gated by RLS (is_sales_member), same belt-and-braces note
        // as deals above. Leads have no standalone name column; company_name
        // carries the second line the picker shows.
        const { data } = await client
          .from('leads')
          .select('id, first_name, last_name, company_name')
          .or(`first_name.ilike.${term},last_name.ilike.${term},company_name.ilike.${term}`)
          .limit(PER_KIND);
        return (data ?? []).map((row) => ({
          id: row.id,
          kind: 'lead' as const,
          label: `${row.first_name} ${row.last_name}`.trim() || row.company_name,
          href: `/tracker?lead=${row.id}`,
          hint:
            row.company_name === ''
              ? KIND_LABELS.lead
              : `${KIND_LABELS.lead} · ${row.company_name}`,
        }));
      })(),
    );
  }

  const results = await Promise.all(lookups);
  return rankMentions(results.flat(), trimmed);
}

/**
 * Best matches first.
 *
 * A prefix match is almost always what somebody meant: typing "Q3" to find
 * "Q3 Gifting" should not be outranked by "Rework Q3 packaging" because that
 * row happened to come back first. Within a tier, shorter labels win — they
 * are the more specific record.
 */
export function rankMentions(items: MentionItem[], query: string): MentionItem[] {
  const needle = query.trim().toLowerCase();
  const score = (item: MentionItem): number => {
    const label = item.label.toLowerCase();
    if (label === needle) return 0;
    if (label.startsWith(needle)) return 1;
    // Word-start beats mid-word: "gift" should find "Q3 Gifting" ahead of
    // "Regifting".
    if (new RegExp(`\\b${needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`).test(label)) return 2;
    return 3;
  };
  return [...items].sort((left, right) => {
    const byScore = score(left) - score(right);
    if (byScore !== 0) return byScore;
    return left.label.length - right.label.length;
  });
}

/**
 * The attributes the badge keeps.
 *
 * The label is stored alongside the id deliberately. A note written in March
 * should still read sensibly in December even if the project has been renamed
 * or deleted — the badge shows what was meant at the time and the link either
 * works or does not. Resolving labels live would rewrite history and turn a
 * deleted record into a blank.
 */
export interface MentionAttrs {
  id: string;
  label: string;
  kind: MentionKind;
  href: string;
}

export function attrsFor(item: MentionItem): MentionAttrs {
  return { id: item.id, label: item.label, kind: item.kind, href: item.href };
}
